import { useMemo, useState } from "react";
import { AutoComplete, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTagSearch } from "@/lib/queries.js";
import { totalCount } from "@/data/category-tree.js";

function useDebounced<T>(value: T, ms = 200): T {
  const [debounced, setDebounced] = useState(value);
  useMemo(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

interface Props {
  variant?: "header" | "hero";
}

export function SearchBox({ variant = "header" }: Props) {
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const debounced = useDebounced(input, 200);
  const { data: tags = [], isFetching } = useTagSearch(debounced);

  const options = tags.map((t) => ({
    value: t.tag,
    label: (
      <span style={{ display: "inline-flex", alignItems: "baseline", gap: 8 }}>
        <span>{t.tag}</span>
        <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>
          {t.count.toLocaleString()}
        </span>
      </span>
    ),
  }));

  function handleSelect(value: string) {
    navigate({ pathname: "/search", search: `?tag=${encodeURIComponent(value)}` });
  }

  const isHero = variant === "hero";
  const trimmed = debounced.trim();

  const notFoundContent = isFetching ? (
    <span style={{ color: "rgba(255,255,255,0.45)", fontSize: 13 }}>Searching...</span>
  ) : trimmed ? (
    <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 13 }}>
      No tags match &ldquo;{trimmed}&rdquo;
    </span>
  ) : (
    <span style={{ color: "rgba(255,255,255,0.45)", fontSize: 13 }}>
      Start typing to search tags
    </span>
  );

  return (
    <AutoComplete
      style={{ width: "100%", maxWidth: isHero ? "100%" : 520 }}
      options={options}
      onSearch={setInput}
      onSelect={handleSelect}
      popupMatchSelectWidth={true}
      notFoundContent={notFoundContent}
    >
      <Input
        size="large"
        allowClear
        prefix={
          <SearchOutlined
            style={{
              color: "rgba(255,255,255,0.5)",
              fontSize: isHero ? 16 : 14,
              marginRight: isHero ? 6 : 0,
            }}
          />
        }
        placeholder={`Search ${totalCount.toLocaleString()} textures...`}
        style={{
          background: isHero ? "rgba(0,0,0,0.35)" : "rgba(255, 255, 255, 0.04)",
          borderColor: isHero ? "rgba(255, 255, 255, 0.12)" : "rgba(255, 255, 255, 0.08)",
          height: isHero ? 48 : undefined,
          fontSize: isHero ? 15 : undefined,
        }}
      />
    </AutoComplete>
  );
}
